// /admin — internal staff dashboard for the Reader's Paper platform.
// Access is gated server-side by ONHERE_ADMIN_EMAILS (staff.RequireStaff);
// non-staff users hitting /admin see the 403 message.
//
// PP-6 base: overview cards, live signals firehose, top-affinity heatmaps
// (community + tag), editor activity, individual reader inspector.
// Reader inspector logs every peek to staff_audit_log per POPIA.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BarSparkline } from '../components/Sparkline';
import { Leaderboard } from '../components/Leaderboard';

interface Overview {
    users_total:          number;
    users_active_7d:      number;
    posts_approved_7d:    number;
    signals_last_24h:     number;
    editor_actions_7d:    number;
    commenter_actions_7d: number;
}
interface Signal {
    id:          number;
    user_id:     string;
    user_name:   string;
    post_id:     string;
    post_title:  string;
    event:       string;
    weight:      number;
    community_id: string | null;
    n_tags:      number;
    created_at:  string;
}
interface AffinityRow {
    scope_id:     string;
    label:        string;
    total_score:  number;
    reader_count: number;
}
interface EditorRow {
    editor_id: string;
    name:      string;
    is_ai:     boolean;
    action:    string;
    count:     number;
}

export function Admin() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [overview, setOverview] = useState<Overview | null>(null);
    const [signals,  setSignals]  = useState<Signal[]>([]);
    const [commAff,  setCommAff]  = useState<AffinityRow[]>([]);
    const [tagAff,   setTagAff]   = useState<AffinityRow[]>([]);
    const [editorRows, setEditorRows] = useState<EditorRow[]>([]);
    const [forbidden, setForbidden] = useState(false);
    const [err, setErr] = useState('');

    const [inspectID, setInspectID] = useState('');
    const [inspection, setInspection] = useState<Record<string, unknown> | null>(null);

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const [ov, sg, ca, ta, ea] = await Promise.all([
                api.get<Overview>          ('/api/admin/overview'),
                api.get<{signals:Signal[]}>('/api/admin/signals?limit=25'),
                api.get<{top:AffinityRow[]}>('/api/admin/affinity/community?limit=10'),
                api.get<{top:AffinityRow[]}>('/api/admin/affinity/tag?limit=10'),
                api.get<{activity:EditorRow[]}>('/api/admin/editor-activity?days=7'),
            ]);
            setOverview(ov);
            setSignals(sg.signals ?? []);
            setCommAff(ca.top ?? []);
            setTagAff(ta.top ?? []);
            setEditorRows(ea.activity ?? []);
        } catch (e) {
            if (e instanceof ApiError && e.status === 403) { setForbidden(true); return; }
            setErr((e as Error).message);
        }
    }, [api, authed]);
    useEffect(() => { load(); }, [load]);

    async function inspect() {
        if (!inspectID) return;
        setInspection(null);
        try {
            const r = await api.get<Record<string, unknown>>(`/api/admin/users/${encodeURIComponent(inspectID)}`);
            setInspection(r);
        } catch (e) {
            setInspection({ error: (e as Error).message });
        }
    }

    if (!authed) return (
        <div className="prelogin">
            <p>Log in.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (forbidden) return (
        <main className="paper-wrap" style={{ padding: 40 }}>
            <h1>Staff only</h1>
            <p>You need to be on the <code>ONHERE_ADMIN_EMAILS</code> allowlist to see this page.</p>
        </main>
    );

    return (
        <main className="paper-wrap" style={{ padding: '20px 24px 60px', maxWidth: 1100 }}>
            <div className="masthead">
                <div className="rule-top" />
                <h1>Admin</h1>
                <div className="issue">Internal staff dashboard · not for public eyes</div>
                <div className="rule-bottom" />
            </div>
            <nav style={adminNavStyle}>
                <Link to="/admin"           style={navLinkStyle}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLinkStyle}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLinkStyle}>Newsroom</Link>
                <Link to="/admin/map"       style={navLinkStyle}>Map</Link>
                <Link to="/admin/claims"    style={navLinkStyle}>Claims</Link>
                <Link to="/admin/analytics" style={navLinkStyle}>Analytics</Link>
            </nav>
            {err && <p style={{ color: '#8b1919' }}>{err}</p>}

            {/* Overview cards */}
            {overview && (
                <div style={cardsGrid}>
                    <Card label="Users total"        value={overview.users_total} />
                    <Card label="Active last 7d"     value={overview.users_active_7d} />
                    <Card label="Posts approved 7d"  value={overview.posts_approved_7d} />
                    <Card label="Signals last 24h"   value={overview.signals_last_24h} />
                    <Card label="Editor actions 7d"  value={overview.editor_actions_7d} />
                    <Card label="Commenter mod 7d"   value={overview.commenter_actions_7d} />
                </div>
            )}

            {/* Two heatmaps side-by-side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 32 }}>
                <section>
                    <h2>Top communities (fresh-decayed)</h2>
                    <Table headers={['Community', 'Score', 'Readers']}
                           rows={commAff.map(a => [a.label, a.total_score.toFixed(1), a.reader_count])} />
                </section>
                <section>
                    <h2>Top tags</h2>
                    <Table headers={['Tag', 'Score', 'Readers']}
                           rows={tagAff.map(a => [a.label, a.total_score.toFixed(1), a.reader_count])} />
                </section>
            </div>

            {/* Editor activity */}
            <section style={{ marginTop: 32 }}>
                <h2>Editor activity (last 7d)</h2>
                {editorRows.length === 0 ? (
                    <p style={{ fontStyle: 'italic', color: '#666' }}>No editor actions in the last 7 days.</p>
                ) : (
                    <Table headers={['Editor', 'AI?', 'Action', 'Count']}
                           rows={editorRows.map(e => [e.name, e.is_ai ? 'yes' : '—', e.action, e.count])} />
                )}
            </section>

            {/* Activity summary — 24h sparklines at the top for a glance view */}
            <ActivitySummary />

            {/* Content flag queue — user reports triaged by staff */}
            <FlagQueue />

            {/* Posts leaderboard — full-featured dashboard (v2). */}
            <Leaderboard onInspect={(userID) => { setInspectID(userID); inspect(); }} />

            {/* Raw signal firehose — tucked away for investigations. Collapsed by default. */}
            <details style={{ marginTop: 32 }}>
                <summary style={{ cursor: 'pointer', fontFamily: "'Newsreader',serif", fontSize: 22 }}>
                    Raw signal firehose ({signals.length})
                </summary>
                <div style={{ marginTop: 8 }}>
                    <button type="button" className="plain" onClick={load} style={{ fontSize: 12 }}>refresh</button>
                    <Table headers={['Event', 'Weight', 'Reader', 'Post', 'When']}
                           rows={signals.map(s => [
                               s.event,
                               s.weight,
                               <button key="i" type="button" style={inspectBtn} onClick={() => { setInspectID(s.user_id); inspect(); }}>
                                   {s.user_name}
                               </button>,
                               s.post_title.slice(0, 50),
                               new Date(s.created_at).toLocaleTimeString(),
                           ])} />
                </div>
            </details>

            {/* Public communities — anon landing target + which communities
                anon visitors can browse. New feature (slice 1 of the public-
                communities rollout); toggles here drive /api/public/* visibility. */}
            <PublicCommunitiesSection />

            {/* Reader inspector */}
            <section style={{ marginTop: 32 }}>
                <h2>Reader inspector</h2>
                <p style={{ color: '#666', fontSize: 12 }}>
                    Every peek is logged to <code>staff_audit_log</code>. The inspected user
                    can request the log at <code>/api/me/audit-log</code>.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={inspectID} onChange={e => setInspectID(e.target.value)}
                           placeholder="user_id (UUID) — or click a name in the firehose"
                           style={{ flex: 1, padding: '6px 10px', fontFamily: 'monospace', fontSize: 12 }} />
                    <button type="button" className="primary" onClick={inspect}
                            style={{ padding: '6px 16px' }}>Inspect</button>
                </div>
                {inspection && (
                    <>
                        <OverrideControls userID={inspectID} onDone={inspect} />
                        <pre style={{ marginTop: 12, padding: 12, background: '#f6f4ea', border: '1px solid #cfc9b7', fontSize: 12, maxHeight: 400, overflow: 'auto' }}>
                            {JSON.stringify(inspection, null, 2)}
                        </pre>
                    </>
                )}
            </section>
        </main>
    );
}

// ActivitySummary — 24h sparklines. First thing a staff member sees when
// they open /admin. Uses BarSparkline for hourly counts.
interface Summary {
    signals:  number[];
    recs:     number[];
    comments: number[];
    flags:    number[];
    editor:   number[];
}

function ActivitySummary() {
    const api = useApi();
    const [s, setS] = useState<Summary | null>(null);
    useEffect(() => {
        api.get<Summary>('/api/admin/activity-summary').then(setS).catch(() => {});
    }, [api]);
    if (!s) return null;
    const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
    return (
        <section style={{ marginTop: 20 }}>
            <h2>Last 24 hours</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <SparkCard label="Signals"       total={sum(s.signals)}  data={s.signals}  color="#5a4a1a" />
                <SparkCard label="Recommends"    total={sum(s.recs)}     data={s.recs}     color="#699a3f" />
                <SparkCard label="Comments"      total={sum(s.comments)} data={s.comments} color="#3d5f8f" />
                <SparkCard label="Flags"         total={sum(s.flags)}    data={s.flags}    color="#8b1919" />
                <SparkCard label="Editor acts"   total={sum(s.editor)}   data={s.editor}   color="#7a5c00" />
            </div>
        </section>
    );
}

function SparkCard({ label, total, data, color }: { label: string; total: number; data: number[]; color: string }) {
    return (
        <div style={{ padding: '10px 12px', border: '1px solid #cfc9b7', background: '#faf8f2', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#666' }}>{label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 22, fontFamily: "'Newsreader',serif" }}>{total}</div>
                <BarSparkline data={data} width={110} height={24} color={color} />
            </div>
        </div>
    );
}


// FlagQueue — PP-11. Shows open user-reports on posts for staff triage.
// One-click resolutions with a shortlist of common outcomes.
interface Flag {
    id:           string;
    post_id:      string;
    post_title:   string;
    author_id:    string;
    flagger_id:   string;
    flagger_name: string;
    reason:       string;
    created_at:   string;
    resolved_at?: string | null;
    resolution?:  string | null;
}

function FlagQueue() {
    const api = useApi();
    const [flags, setFlags] = useState<Flag[]>([]);
    const [status, setStatus] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ flags: Flag[] }>('/api/admin/flags?status=open&limit=50');
            setFlags(r.flags ?? []);
        } catch (e) { setStatus((e as Error).message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function resolve(id: string, resolution: string) {
        try {
            await api.post(`/api/admin/flags/${id}/resolve`, { resolution });
            load();
        } catch (e) { setStatus((e as Error).message); }
    }

    return (
        <section style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2>Content flag queue ({flags.length})</h2>
                <button type="button" className="plain" onClick={load} style={{ fontSize: 12 }}>refresh</button>
            </div>
            {status && <p style={{ color: '#8b1919' }}>{status}</p>}
            {flags.length === 0 ? (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No open flags. Clean day.</p>
            ) : (
                <Table headers={['Post', 'Reason', 'Flagger', 'When', 'Resolve']}
                       rows={flags.map(f => [
                           <a key="p" href={`/posts/${f.post_id}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
                               {f.post_title.slice(0, 40)}
                           </a>,
                           f.reason.slice(0, 60),
                           f.flagger_name,
                           new Date(f.created_at).toLocaleString(),
                           <div key="r" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                               <button type="button" style={miniBtn} onClick={() => resolve(f.id, 'no_action')}>No action</button>
                               <button type="button" style={miniBtn} onClick={() => resolve(f.id, 'content_removed')}>Removed</button>
                               <button type="button" style={miniBtn} onClick={() => resolve(f.id, 'user_warned')}>Warned</button>
                           </div>,
                       ])} />
            )}
        </section>
    );
}
const miniBtn: React.CSSProperties = {
    padding: '2px 6px', border: '1px solid #666', background: '#fff', fontSize: 11, cursor: 'pointer',
};

// OverrideControls — the PP-10 staff toolkit on a specific user. All actions
// require a reason (prompted); every call lands in staff_audit_log; the
// inspected user sees each override in /api/me/audit-log.
function OverrideControls({ userID, onDone }: { userID: string; onDone: () => void }) {
    const api = useApi();
    const [status, setStatus] = useState('');
    const [busy, setBusy] = useState(false);

    async function act(fn: () => Promise<unknown>, label: string) {
        setBusy(true); setStatus('');
        try { await fn(); setStatus('✓ ' + label); onDone(); }
        catch (e) { setStatus('✗ ' + (e as Error).message); }
        finally { setBusy(false); }
    }
    function ask(prompt: string): string | null {
        const r = window.prompt(prompt);
        return r && r.trim() ? r.trim() : null;
    }

    return (
        <div style={{ marginTop: 12, padding: 10, border: '1px dashed #8b1919', background: '#fdf3f3' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#8b1919', marginBottom: 8 }}>
                Overrides — every action audited, user sees it
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" disabled={busy} style={dangerBtn} onClick={() => {
                    const reason = ask('Suspend this user — reason:');
                    if (reason) act(() => api.post(`/api/admin/users/${userID}/suspend`, { reason }), 'suspended');
                }}>Suspend</button>
                <button type="button" disabled={busy} style={dangerBtn} onClick={() => {
                    const reason = ask('Shadow-ban this user (posts hidden from everyone else) — reason:');
                    if (reason) act(() => api.post(`/api/admin/users/${userID}/suspend`, { reason, shadow: true }), 'shadow-banned');
                }}>Shadow-ban</button>
                <button type="button" disabled={busy} style={softBtn} onClick={() => {
                    const reason = ask('Unsuspend / un-shadow-ban — reason:');
                    if (reason) act(() => api.post(`/api/admin/users/${userID}/unsuspend`, { reason }), 'unsuspended');
                }}>Unsuspend</button>
                <button type="button" disabled={busy} style={softBtn} onClick={() => {
                    const reason = ask('Reset affinity vectors (nuke reader_affinity rows) — reason:');
                    if (reason) act(() => api.post(`/api/admin/users/${userID}/reset-affinity`, { reason }), 'affinity wiped');
                }}>Reset affinity</button>
            </div>
            {status && <div style={{ marginTop: 8, fontSize: 12, color: status.startsWith('✓') ? 'var(--accent)' : '#8b1919' }}>{status}</div>}
        </div>
    );
}

const dangerBtn: React.CSSProperties = {
    padding: '4px 10px', border: '1px solid #8b1919', background: '#fff', color: '#8b1919',
    fontSize: 12, cursor: 'pointer', fontFamily: '-apple-system, sans-serif',
};
const softBtn: React.CSSProperties = {
    padding: '4px 10px', border: '1px solid #666', background: '#fff', color: '#666',
    fontSize: 12, cursor: 'pointer', fontFamily: '-apple-system, sans-serif',
};

const cardsGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12, marginTop: 20,
};
const adminNavStyle: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '10px 0 6px', marginBottom: 16,
    borderBottom: '1px solid #cfc9b7',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 2,
};
const navLinkStyle: React.CSSProperties = {
    color: '#111', textDecoration: 'none', padding: '4px 10px',
    border: '1px solid transparent', fontWeight: 700,
};
const inspectBtn: React.CSSProperties = {
    background: 'none', border: 'none', color: 'var(--accent)',
    cursor: 'pointer', padding: 0, font: 'inherit', textAlign: 'left',
};

function Card({ label, value }: { label: string; value: number | string }) {
    return (
        <div style={{ padding: '14px 16px', border: '1px solid #cfc9b7', background: '#faf8f2' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#666' }}>{label}</div>
            <div style={{ fontSize: 28, fontFamily: "'Newsreader', serif" }}>{value}</div>
        </div>
    );
}

function Table({ headers, rows }: { headers: string[]; rows: (string | number | React.ReactNode)[][] }) {
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '-apple-system, sans-serif', fontSize: 13 }}>
            <thead>
                <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                    {headers.map(h => <th key={h} style={{ padding: '6px 8px' }}>{h}</th>)}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        {row.map((cell, j) => <td key={j} style={{ padding: '6px 8px' }}>{cell}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// PublicCommunitiesSection — staff UI for the two knobs that determine
// what anon visitors see on OnHere:
//   1. `public` per community — controls anon visibility of that community.
//   2. `default_community` singleton — which community shows on the unauth
//      landing (onhere.cc/) when no other route is requested.
// Read-through /api/public/* endpoints enforce these flags in SQL.
interface AdminCommunityRow {
    id:         string;
    slug:       string;
    name:       string;
    active:     boolean;
    public:     boolean;
    is_default: boolean;
}

function PublicCommunitiesSection() {
    const api = useApi();
    const [rows, setRows]     = useState<AdminCommunityRow[]>([]);
    const [busy, setBusy]     = useState<string>('');   // id being mutated
    const [err,  setErr]      = useState<string>('');

    const load = useCallback(async () => {
        try {
            const r = await api.get<{communities: AdminCommunityRow[]}>('/api/admin/site/communities');
            setRows(r.communities ?? []);
            setErr('');
        } catch (e) { setErr((e as Error).message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function togglePublic(row: AdminCommunityRow) {
        setBusy(row.id + ':public');
        try {
            await api.put(`/api/admin/site/communities/${encodeURIComponent(row.slug)}/public`, { on: !row.public });
            await load();
        } catch (e) { setErr((e as Error).message); }
        finally    { setBusy(''); }
    }

    async function setDefault(row: AdminCommunityRow | null) {
        setBusy((row?.id ?? 'none') + ':default');
        try {
            await api.put('/api/admin/site/default-community', { community_id: row?.id ?? null });
            await load();
        } catch (e) { setErr((e as Error).message); }
        finally    { setBusy(''); }
    }

    return (
        <section style={{ marginTop: 32 }}>
            <h2>Public communities</h2>
            <p style={{ color: '#666', fontSize: 12, margin: '0 0 12px' }}>
                Only <strong>public</strong> communities are visible to signed-out visitors.
                The <strong>default</strong> community renders on the unauth landing at{' '}
                <code>onhere.cc/</code>. A community must be public before it can be default.
            </p>
            {err && <p style={{ color: '#8b1919' }}>{err}</p>}
            {rows.length === 0 ? (
                <p style={{ fontStyle: 'italic', color: '#666' }}>No communities exist yet.</p>
            ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>Community</th>
                            <th style={{ padding: '6px 8px' }}>Slug</th>
                            <th style={{ padding: '6px 8px' }}>Active</th>
                            <th style={{ padding: '6px 8px' }}>Public</th>
                            <th style={{ padding: '6px 8px' }}>Default landing</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '6px 8px' }}>{row.name}</td>
                                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{row.slug}</td>
                                <td style={{ padding: '6px 8px' }}>{row.active ? 'yes' : '—'}</td>
                                <td style={{ padding: '6px 8px' }}>
                                    <label style={{ cursor: 'pointer' }}>
                                        <input type="checkbox"
                                               checked={row.public}
                                               disabled={busy !== ''}
                                               onChange={() => togglePublic(row)} />
                                        {' '}{row.public ? 'public' : 'private'}
                                    </label>
                                </td>
                                <td style={{ padding: '6px 8px' }}>
                                    <label style={{ cursor: row.public ? 'pointer' : 'not-allowed', opacity: row.public ? 1 : 0.4 }}>
                                        <input type="radio"
                                               name="default-community"
                                               checked={row.is_default}
                                               disabled={busy !== '' || !row.public}
                                               onChange={() => setDefault(row)} />
                                        {' '}{row.is_default ? 'landing page' : ''}
                                    </label>
                                </td>
                            </tr>
                        ))}
                        <tr style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '6px 8px', fontStyle: 'italic', color: '#666' }}>No default</td>
                            <td colSpan={3} />
                            <td style={{ padding: '6px 8px' }}>
                                <label style={{ cursor: 'pointer' }}>
                                    <input type="radio"
                                           name="default-community"
                                           checked={!rows.some(r => r.is_default)}
                                           disabled={busy !== ''}
                                           onChange={() => setDefault(null)} />
                                    {' '}(unauth landing → 404-empty)
                                </label>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
        </section>
    );
}
